import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Emblem } from "@/components/Emblem";
import { LuxButton } from "@/components/LuxButton";
import { EASE } from "@/components/Reveal";
import { Page } from "@/components/Page";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await login(email, password);
      navigate(data.role === "admin" ? "/admin" : "/account");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    if (!email) {
      toast.info("Enter your email first, and we will send reset instructions.");
      return;
    }
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <Page testId="login-page">
      <section className="relative flex min-h-screen items-center justify-center overflow-hidden px-6 py-28">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 60% 50% at 50% 0%, rgba(200,164,93,0.06), transparent 65%), #050505",
          }}
        />
        <motion.div
          className="relative w-full max-w-md border border-[rgba(200,164,93,0.25)] bg-[#0B0B0B]/90 px-8 py-12 sm:px-12"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE }}
        >
          <div className="flex flex-col items-center">
            <Emblem className="h-14 w-auto" />
            <div className="gold-hairline mt-6 w-20" />
            <h1 className="mt-7 font-serif text-3xl font-medium text-ivory sm:text-4xl">
              Welcome <span className="gold-text italic">back.</span>
            </h1>
            <p className="mt-3 text-[11px] font-light uppercase tracking-[0.3em] text-ivory-muted/70">
              Member Portal
            </p>
          </div>

          <form onSubmit={submit} data-testid="login-form" className="mt-10 space-y-7">
            <div>
              <label htmlFor="login-email" className="lux-label">Email</label>
              <input
                id="login-email"
                data-testid="login-email-input"
                type="email"
                required
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="lux-input"
                placeholder="you@example.com"
              />
            </div>
            <div>
              <label htmlFor="login-password" className="lux-label">Password</label>
              <input
                id="login-password"
                data-testid="login-password-input"
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="lux-input"
                placeholder="••••••••"
              />
            </div>
            {error && (
              <p data-testid="login-error" className="text-[12px] font-light text-red-400/90">
                {error}
              </p>
            )}
            <LuxButton type="submit" variant="gold" disabled={busy} testId="login-submit-button" className="w-full">
              {busy ? "Signing in" : "Login"}
            </LuxButton>
          </form>

          <div className="mt-8 flex items-center justify-between text-[11px] font-light tracking-wide">
            <button
              type="button"
              onClick={forgot}
              data-testid="login-forgot-password"
              className="text-ivory-muted/80 transition-colors duration-300 hover:text-champagne"
            >
              Forgot password
            </button>
            <Link
              to="/"
              data-testid="login-back-home"
              className="text-ivory-muted/80 transition-colors duration-300 hover:text-champagne"
            >
              Return to site
            </Link>
          </div>
        </motion.div>
      </section>
    </Page>
  );
}
